import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";

import { defined } from "@formant/core/common/defined";
import { IView } from "@formant/core/model/IView";
import { Uuid } from "@formant/core/model/Uuid";
import { highlights } from "@formant/ui/component/common/Colors";
import { Typography } from "@formant/ui/component/common/Typography";

import { useRouter } from "../../context/RouterContext";
import { useStores } from "../../context/StoreContext";
import { useViewContext } from "../../context/TeleopContext/ViewContext";
import { appStore } from "../../stores";
import { IconButton } from "../common/IconButton";
import { ModuleAddAnalytics } from "../common/ModuleAddAnalytics";
import { ModuleAddDashboard } from "../common/ModuleAddDashboard";
import { StandardButton } from "../common/StandardButton";

interface IEditViewHeadersProps {
    view?: IView;
    deviceIds: Uuid[];
    children?: React.ReactNode;
    isV2Dashboard?: boolean;
}

export const EditViewHeader: React.FC<IEditViewHeadersProps> = observer(
    function EditViewHeader(props) {
        const { view, children, deviceIds, isV2Dashboard } = props;
        const { navigate, routeTo } = useRouter();

        const { observeStore } = useStores();
        const { isViewFocused, setIsViewFocused, onRevertChanges } =
            useViewContext();

        const editMode =
            observeStore.configuringAnalyticsId ||
            observeStore.configuringModule;

        const layoutType = view && view?.layoutType;
        const isAnalytics = layoutType === "analytics";

        if (!view || !view.id) {
            return null;
        }

        const handleReset = async () => {
            appStore.setConfirm({
                title: `Are you sure you wish to cancel changes?`,
                message: <div />,
                okCopy: "yes",
                cancelCopy: "no",
                onOk: () => {
                    appStore.snackbar = undefined;
                    onRevertChanges();
                },
            });
        };

        const handleSettings = async () => {
            await navigate(routeTo.editView({ id: defined(view.id) }));
        };

        return (
            <>
                <div>{children}</div>
                <Middle $border={!isV2Dashboard ? !editMode : isViewFocused}>
                    <Typography type="h2" color="silver">
                        {view.name}
                    </Typography>
                    {isV2Dashboard && (
                        <IconButton
                            icon="edit"
                            onClick={() => {
                                setIsViewFocused(true);
                            }}
                        />
                    )}
                </Middle>

                <Right>
                    {isAnalytics && (
                        <ModuleAddAnalytics deviceIds={deviceIds} />
                    )}
                    {layoutType === "dashboard" && (
                        <ModuleAddDashboard deviceIds={deviceIds} hide={true} />
                    )}
                    {layoutType !== "analytics" &&
                        layoutType !== "dashboard" && (
                            <SettingsButton
                                dark={true}
                                onClick={handleSettings}
                            >
                                <Typography type="label">
                                    view settings
                                </Typography>
                            </SettingsButton>
                        )}

                    {layoutType === "analytics" &&
                        (observeStore.configuringAnalyticsId ||
                            observeStore.isCustomizing) && (
                            <SettingsButton
                                critical={true}
                                onClick={handleReset}
                            >
                                <Typography type="label">reset</Typography>
                            </SettingsButton>
                        )}
                </Right>
            </>
        );
    },
);

const Middle = styled.div<{ $border: boolean }>`
    display: flex;
    gap: 0.5rem;
    margin: 0 auto;
    border-bottom: ${props =>
        props.$border ? `0.125rem solid ${highlights.blue}` : "none"};
`;

const Right = styled.div`
    margin-left: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SettingsButton = styled(StandardButton)`
    margin-right: 1rem;
    margin-left: 1rem;
`;
